import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import type { AccessibilityCssVars, AccessibilityPresetId, AccessibilitySettings } from '@shared/accessibility/types'
import { resolveAccessibility } from '@shared/accessibility/presets'
import { useAuth } from '../../contexts/auth-context'
import {
  getAccessibilitySettings,
  saveAccessibilitySettings,
} from '../../services/profile-service'

type AccessibilityContextValue = {
  settings: AccessibilitySettings
  presetId: AccessibilityPresetId
  simplifiedNav: boolean
  reducedMotion: boolean
  highContrast: boolean
  voiceHints: boolean
  setPreset: (preset: AccessibilityPresetId) => Promise<void>
  saving: boolean
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null)

function applyDocumentAccessibility(
  presetId: AccessibilityPresetId,
  cssVars: AccessibilityCssVars,
  flags: { reducedMotion: boolean; highContrast: boolean },
): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.dataset.a11yPreset = presetId
  if (flags.reducedMotion) root.dataset.a11yReducedMotion = 'true'
  else delete root.dataset.a11yReducedMotion
  if (flags.highContrast) root.dataset.a11yHighContrast = 'true'
  else delete root.dataset.a11yHighContrast
  for (const [key, value] of Object.entries(cssVars) as [keyof AccessibilityCssVars, string][]) {
    root.style.setProperty(key, value)
  }
}

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const { user, refresh } = useAuth()
  const settings = useMemo(
    () => getAccessibilitySettings(user ?? undefined),
    [user],
  )
  const resolved = useMemo(() => resolveAccessibility(settings), [settings])

  useEffect(() => {
    applyDocumentAccessibility(resolved.preset.id, resolved.cssVars, {
      reducedMotion: resolved.reducedMotion,
      highContrast: resolved.highContrast,
    })
  }, [resolved])

  const setPreset = useCallback(
    async (preset: AccessibilityPresetId) => {
      await saveAccessibilitySettings({ preset })
      await refresh()
    },
    [refresh],
  )

  const value = useMemo<AccessibilityContextValue>(
    () => ({
      settings,
      presetId: resolved.preset.id,
      simplifiedNav: resolved.simplifiedNav,
      reducedMotion: resolved.reducedMotion,
      highContrast: resolved.highContrast,
      voiceHints: resolved.voiceHints,
      setPreset,
      saving: false,
    }),
    [settings, resolved, setPreset],
  )

  return (
    <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>
  )
}

export function useAccessibility(): AccessibilityContextValue {
  const ctx = useContext(AccessibilityContext)
  if (!ctx) {
    throw new Error('useAccessibility must be used within AccessibilityProvider')
  }
  return ctx
}

export function useAccessibilityOptional(): AccessibilityContextValue | null {
  return useContext(AccessibilityContext)
}
